import React, { useState, useEffect, useCallback } from 'react';
import { Form, Row, Col, Image as RBImage } from 'react-bootstrap';
import { FaCalendar, FaMapMarkerAlt, FaWeight, FaBullseye, FaArrowLeft } from 'react-icons/fa';
import { GiDeerTrack } from 'react-icons/gi';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { huntService } from '../services/huntService';
import api from '../../../utils/api';
import { HuntSignOutService } from '../../map/services/huntSignOutService';
import { toLocalYMD } from '../../../utils/dateUtils';
import { weatherService } from '../../map/services/weatherService';
import { emitHuntCompleted } from '../../../utils/huntEvents';
import { useToast } from '../../../components/ui/ToastProvider';
import safeStorage from '../../../utils/safeStorage';
// V2 enabled: force V2 on this page
import { YButton } from '../../../ui/YButton';
import { YCard } from '../../../ui/YCard';
import { YFormField } from '../../../ui/YFormField';
import { YInput } from '../../../ui/YInput';
import { YSelect } from '../../../ui/YSelect';
import { YCheckbox } from '../../../ui/YCheckbox';
import { YTextarea } from '../../../ui/YTextarea';
import { AssetPickerModal } from '../../../components/common/AssetPickerModal';
import { HuntSectionHeader } from './HuntSectionHeader';
import './hunts-shell.css';

// Location options
const locationOptions = [
  { value: 'Private Spot', label: 'Private Spot' },
  // TODO: Add club stands here when available
];

// Function to get coordinates for weather data
const getCoordinates = (selectedLocation) => {
  // First, check if the selected location has predefined coordinates
  const locationOption = locationOptions.find(option => option.value === selectedLocation);
  if (locationOption && locationOption.coords) {
    return locationOption.coords;
  }
  
  // Fallback to map coordinates from persistent storage
  try {
    const mapState = safeStorage.getItem('mapState');
    if (mapState) {
      const parsed = JSON.parse(mapState);
      if (parsed.center && parsed.center.length === 2) {
        return {
          lat: parsed.center[1],
          lng: parsed.center[0]
        };
      }
    }
  } catch (error) {
    console.warn('Could not parse map state:', error);
  }
  return null;
};

const HuntLogPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { show } = useToast();
  const { huntId } = useParams();
  const [hunt, setHunt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isV2 = true;
  const formLabelClass = 'y-label--dialog';
  
  // Add weather state HERE (inside the component)
  const [weatherData, setWeatherData] = useState({});
  const [weatherLoading, setWeatherLoading] = useState(false);
  
  // Add state for available locations (grouped tokens)
  const [availableLocations, setAvailableLocations] = useState([
    { value: 'personal', label: 'Private Spot', group: 'Personal' }
  ]);
  
  const [otherClubSpotName, setOtherClubSpotName] = useState('');
  
  const [formData, setFormData] = useState({
    date: toLocalYMD(new Date()),
    time_periods: [],
    location: 'personal',
    location_type: 'private_spot',
    does_seen: 0,
    bucks_seen: 0,
    fawns_seen: 0,
    shot_taken: false,
    harvest: false,
    notes: ''
  });
  
  const [harvestData, setHarvestData] = useState({
    deer_type: 'buck',
    method: 'gun',
    weight: '',
    points: '',
    notable_features: ''
  });
  
  const [harvestPhotoFile, setHarvestPhotoFile] = useState(null);
  const [harvestPhotoPreview, setHarvestPhotoPreview] = useState(null);
  const [harvestPhotoCaption, setHarvestPhotoCaption] = useState('');
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null); // { id, url, thumb_url }
  
  const [errors, setErrors] = useState({});
  
  const timePeriodOptions = [
    { value: 'morning', label: 'Morning' },
    { value: 'midday', label: 'Midday' },
    { value: 'afternoon', label: 'Afternoon' },
    { value: 'all_day', label: 'All Day' }
  ];
  
  // Add ref to track if weather has been fetched
  // const weatherFetchedRef = useRef(false);
  
  // Wrap fetchHuntWeather with useCallback to prevent recreation
  const fetchHuntWeather = useCallback(async (location, date, timePeriods) => {
    if (!location || !date || !timePeriods.length) return null;
    
    try {
      setWeatherLoading(true);
      
      // Get coordinates from the selected location
      let lat, lng;
      
      if (location === 'personal') {
        // For private spots, use map center from stored map state
        const coordinates = getCoordinates('personal');
        if (coordinates) {
          lat = coordinates.lat;
          lng = coordinates.lng;
        } else {
          console.warn('No coordinates available for weather fetch');
          return null;
        }
      } else {
        // For club locations, get coordinates from the hunt location
        console.log('🏢 Processing Club Location:', location);
        console.log('🔍 Current availableLocations state:', availableLocations);
        
        // Use the current availableLocations state (value token)
        const huntLocation = availableLocations.find(loc => loc.value === location);
        
        console.log('🔍 Hunt location search result:', huntLocation);
        
        if (huntLocation) {
          if (huntLocation.geojson) {
            // Use coordinates directly from the location object
            [lng, lat] = huntLocation.geojson.coordinates;
            console.log('✅ Club location coordinates found:', { lat, lng });
          } else if (huntLocation.locationId) {
            // Fetch the actual hunt location data using the locationId
            try {
              const response = await api.get(`/hunt-locations/${huntLocation.locationId}/`);
              if (response.data && response.data.geojson) {
                [lng, lat] = response.data.geojson.coordinates;
                console.log('✅ Hunt location coordinates fetched via API:', { lat, lng });
              } else {
                console.warn('❌ No geojson data in API response:', response.data);
                return null;
              }
            } catch (apiError) {
              console.error('❌ Failed to fetch hunt location data:', apiError);
              return null;
            }
          } else {
            console.warn('❌ No geojson data or locationId found in hunt location');
            return null;
          }
        } else {
          console.warn('❌ Hunt location not found in available locations');
          return null;
        }
      }
      
      // Now fetch the actual weather data using the coordinates
      console.log('🌤️ Fetching weather for coordinates:', { lat, lng, date, timePeriods });
      const weather = await weatherService.getHuntWeatherData(lat, lng, date, timePeriods);
      
      console.log('🌤️ Weather data received:', weather);
      
      // Store weather data in component state
      setWeatherData(weather);
      
      // Return the weather data so it can be used in submission
      return weather;
      
    } catch (error) {
      console.error('Failed to fetch hunt weather:', error);
      return null;
    } finally {
      setWeatherLoading(false);
    }
  }, [availableLocations]); // Only depend on availableLocations
  
  // Load hunt data if editing
  useEffect(() => {
    if (huntId) {
      const loadHunt = async () => {
        try {
          setLoading(true);
          const huntData = await huntService.getHunt(huntId);
          setHunt(huntData);
        } catch (err) {
          setError('Failed to load hunt data');
          console.error('Error loading hunt:', err);
        } finally {
          setLoading(false);
        }
      };
      loadHunt();
    }
  }, [huntId]);
  
  // Load clubs (with hunt_locations) to build grouped selector
  useEffect(() => {
    const loadClubsAndLocations = async () => {
      try {
        const resp = await api.get('/clubs/');
        const clubsData = resp.data || [];
        const options = [{ value: 'personal', label: 'Private Spot', group: 'Personal' }];
        clubsData.forEach(club => {
          const clubName = club.name || `Club ${club.id}`;
          // Other (Club)
          options.push({ value: `club-other:${club.id}`, label: `Other (${clubName})`, group: clubName, clubId: club.id, isOtherClub: true });
          // Specific locations
          (club.hunt_locations || []).forEach(loc => {
            options.push({
              value: `loc:${loc.id}`,
              label: loc.name,
              group: clubName,
              locationId: loc.id,
              clubId: club.id,
              geojson: loc.geojson
            });
          });
        });
        setAvailableLocations(options);
      } catch (e) {
        console.error('Failed to load clubs/locations', e);
        setAvailableLocations([{ value: 'personal', label: 'Private Spot', group: 'Personal' }]);
      }
    };
    loadClubsAndLocations();
  }, []);
  
  // Initialize form with hunt data if editing
  useEffect(() => {
    if (hunt) {
      setFormData({
        date: hunt.date,
        time_periods: hunt.time_periods || [],
        location: hunt.location,
        location_type: hunt.location_type,
        does_seen: hunt.does_seen,
        bucks_seen: hunt.bucks_seen,
        fawns_seen: hunt.fawns_seen,
        shot_taken: hunt.shot_taken,
        harvest: hunt.harvest,
        notes: hunt.notes || ''
      });
      
      if (hunt.harvest_details) {
        setHarvestData({
          deer_type: hunt.harvest_details.deer_type,
          method: hunt.harvest_details.method || 'gun',
          weight: hunt.harvest_details.weight || '',
          points: hunt.harvest_details.points || '',
          notable_features: hunt.harvest_details.notable_features || ''
        });
      }
    }
    setErrors({});
  }, [hunt]);
  
  // Check for pre-filled data from navigation state
  useEffect(() => {
    const prefillData = location.state?.prefillData;
    
    if (prefillData && !huntId) { // Only for new hunts, not editing
      console.log('Pre-filling hunt log with data:', prefillData);
      let mappedLocation = 'personal';
      if (prefillData.hunt_location) {
        mappedLocation = `loc:${prefillData.hunt_location}`;
      } else if (prefillData.location_type === 'private_spot') {
        mappedLocation = 'personal';
      } else if (prefillData.location) {
        const match = availableLocations.find(o => o.label === prefillData.location);
        if (match) mappedLocation = match.value;
      }
      setFormData(prev => ({
        ...prev,
        date: prefillData.date || prev.date,
        location: mappedLocation,
        location_type: prefillData.location_type || prev.location_type,
        time_periods: prefillData.time_periods || prev.time_periods,
        // Keep other fields as defaults (user fills these)
        does_seen: 0,
        bucks_seen: 0,
        fawns_seen: 0,
        shot_taken: false,
        harvest: false,
        notes: ''
      }));
    }
  }, [location.state, huntId, availableLocations]);
  
  // Check if this hunt is from a sign-out (to auto sign-in after submission)
  const isFromSignOut = location.state?.fromSignOut;
  const signOutDetails = location.state?.prefillData?.sign_out_details;
  const huntLocationId = location.state?.prefillData?.hunt_location;
  
  // Add debugging for these variables
  useEffect(() => {
    console.log('=== HUNT LOG PAGE DEBUG ===');
    console.log('isFromSignOut:', isFromSignOut);
    console.log('signOutDetails:', signOutDetails);
    console.log('huntLocationId:', huntLocationId);
    console.log('location.state:', location.state);
    console.log('prefillData:', location.state?.prefillData);
  }, [isFromSignOut, signOutDetails, huntLocationId, location.state]);
  
// Update your weather auto-fetch useEffect
useEffect(() => {
  console.log('🌤️ === WEATHER AUTO-FETCH DEBUG START ===');
  console.log('🔍 location.state:', location.state);
  console.log('🌤️ availableLocations length:', availableLocations.length);
  console.log('🌤️ availableLocations:', availableLocations.map(loc => ({ name: loc.name, value: loc.value })));
  
  // Only proceed if we have both prefill data AND available locations
  if (location.state?.prefillData && availableLocations.length > 1) { // Changed from > 0 to > 1
    const prefillData = location.state.prefillData;
    console.log('✅ Prefill data found:', prefillData);
    
    // Extract the data you need
    const { date, location: huntLocation, time_periods } = prefillData;
    console.log('🔍 Extracted hunt data:', { date, huntLocation, time_periods });
    
    // Check if the hunt location exists in available locations
    const foundLocation = availableLocations.find(loc => 
      loc.name === huntLocation || loc.value === huntLocation
    );
    
    if (foundLocation) {
      console.log('✅ Hunt location found in available locations:', foundLocation);
      
      // Call fetchHuntWeather if you have the data
      if (date && huntLocation && time_periods) {
        console.log('🌤️ Auto-fetching weather for pre-filled hunt:', { date, huntLocation, time_periods });
        fetchHuntWeather(huntLocation, date, time_periods);
      } else {
        console.log('❌ Missing data for weather fetch:', { date, huntLocation, time_periods });
      }
    } else {
      console.log('❌ Hunt location not found in available locations:', huntLocation);
      console.log('🌤️ Available location names:', availableLocations.map(loc => loc.name));
    }
  } else {
    if (!location.state?.prefillData) {
      console.log('❌ No prefill data found');
    }
    if (availableLocations.length <= 1) {
      console.log('❌ Available locations not fully loaded yet (only have:', availableLocations.length, 'locations)');
    }
  }
  
  console.log('🌤️ === WEATHER AUTO-FETCH DEBUG END ===');
}, [location.state, availableLocations, fetchHuntWeather]); // Dependencies include availableLocations
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    
    // Auto-check "Shot taken" when "Harvest" is selected
    if (field === 'harvest' && value === true) {
      setFormData(prev => ({ ...prev, shot_taken: true }));
    }
  };
  
  const handleTimePeriodChange = (period) => {
    setFormData(prev => ({
      ...prev,
      time_periods: prev.time_periods.includes(period)
        ? prev.time_periods.filter(p => p !== period)
        : [...prev.time_periods, period]
    }));
  };
  
  const handleHarvestChange = (field, value) => {
    setHarvestData(prev => ({ ...prev, [field]: value }));
    if (errors[`harvest_${field}`]) {
      setErrors(prev => ({ ...prev, [`harvest_${field}`]: null }));
    }
  };
  
  // Client-side downscale to max 1600px longest edge and build 320px thumbnail
  const downscaleImage = async (file, maxSize) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        const ratio = width / height;
        if (width > height) {
          if (width > maxSize) { height = Math.round(maxSize / ratio); width = maxSize; }
        } else {
          if (height > maxSize) { width = Math.round(maxSize * ratio); height = maxSize; }
        }
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (!blob) { reject(new Error('Image compression failed')); return; }
          resolve({ blob, width, height });
        }, 'image/jpeg', 0.8);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };
  
  const buildThumbnail = async (file) => {
    return downscaleImage(file, 320); // returns blob, width, height (width/height ignored for thumb)
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.date) newErrors.date = 'Date is required';
    if (formData.time_periods.length === 0) newErrors.time_periods = 'At least one time period is required';
    if (!formData.location) newErrors.location = 'Location is required';
    
    // Validate deer counts
    if (formData.does_seen < 0) newErrors.does_seen = 'Count cannot be negative';
    if (formData.bucks_seen < 0) newErrors.bucks_seen = 'Count cannot be negative';
    if (formData.fawns_seen < 0) newErrors.fawns_seen = 'Count cannot be negative';
    
    // Validate harvest data if harvest is true
    if (formData.harvest) {
      if (!harvestData.deer_type) newErrors.harvest_deer_type = 'Deer type is required';
      if (harvestData.deer_type === 'buck' && (!harvestData.points || harvestData.points < 0)) {
        newErrors.harvest_points = 'Points are required for bucks and must be positive';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      console.log('🌤️ === HUNT SUBMISSION START ===');
      console.log('🌤️ Current weather data in state:', weatherData);
      console.log('🌤️ Weather data type:', typeof weatherData);
      console.log('🌤️ Weather data keys:', weatherData ? Object.keys(weatherData) : 'null');
      console.log('🌤️ Weather data content:', JSON.stringify(weatherData, null, 2));
      
      // Use existing weather data if available, otherwise fetch it
      let finalWeatherData = weatherData;
      
      if (Object.keys(weatherData).length === 0) {
        console.log('🌤️ No weather data in state, fetching now...');
        finalWeatherData = await fetchHuntWeather(
          formData.location, 
          formData.date, 
          formData.time_periods
        );
        console.log('🌤️ Weather data after fetch:', finalWeatherData);
      } else {
        console.log('🌤️ Using existing weather data from state');
      }
      
      console.log('🌤️ Final weather data for submission:', finalWeatherData);
      console.log('🌤️ Final weather data type:', typeof finalWeatherData);
      
      // Map selection to club/location ids and labels
      let clubId = null;
      let huntLocationIdForSubmit = null;
      let locationType = 'private_spot';
      let locationLabel = 'Private Spot';
      const selectedOpt = availableLocations.find(o => o.value === formData.location);
      if (selectedOpt) {
        if (selectedOpt.value === 'personal') {
          locationType = 'private_spot';
          locationLabel = 'Private Spot';
        } else if (selectedOpt.value.startsWith('loc:')) {
          locationType = 'club_location';
          locationLabel = selectedOpt.label;
          clubId = selectedOpt.clubId || null;
          huntLocationIdForSubmit = selectedOpt.locationId;
        } else if (selectedOpt.value.startsWith('club-other:')) {
          locationType = 'club_location';
          clubId = selectedOpt.clubId;
          locationLabel = otherClubSpotName && otherClubSpotName.trim() ? otherClubSpotName.trim() : selectedOpt.label;
        }
      }
      
      // Prepare hunt data with weather
      const submitData = { 
        ...formData,
        location: locationLabel,
        location_type: locationType,
        club_id: clubId,
        hunt_location_id: huntLocationIdForSubmit,
        weather_data: finalWeatherData || {}
      };
      
      console.log('📝 Final hunt data being submitted:', submitData);
      console.log('📝 Weather data in submitData:', submitData.weather_data);
      console.log('📝 Weather data keys in submitData:', Object.keys(submitData.weather_data));
      
      // Add harvest details if harvest is true
      if (formData.harvest) {
        submitData.harvest_details = {
          ...harvestData,
          weight: harvestData.weight ? parseFloat(harvestData.weight) : null,
          points: harvestData.points ? parseInt(harvestData.points) : null
        };
      }
      
      let effectiveHuntId = huntId || null;
      let updatedHuntObj = null;
      if (huntId) {
        console.log('🔄 Updating existing hunt with data:', submitData);
        updatedHuntObj = await huntService.updateHunt(huntId, submitData);
        try { show({ title: 'Hunt updated', body: 'Your hunt log was updated successfully.', variant: 'success' }); } catch (e) {}
      } else {
        // Get coordinates for weather data based on selected option
        let coordinates = null;
        if (selectedOpt) {
          if (selectedOpt.value === 'personal') {
            coordinates = getCoordinates('personal');
          } else if (selectedOpt.geojson && selectedOpt.geojson.coordinates) {
            coordinates = { lat: selectedOpt.geojson.coordinates[1], lng: selectedOpt.geojson.coordinates[0] };
          } else {
            coordinates = getCoordinates('personal');
          }
        }
        console.log('🆕 Creating new hunt with data:', submitData);
        console.log('📍 Coordinates for hunt:', coordinates);
        const created = await huntService.createHunt(submitData, coordinates);
        updatedHuntObj = created || null;
        effectiveHuntId = created?.id || null;
        try { show({ title: 'Hunt logged', body: 'Your hunt has been saved successfully.', variant: 'success' }); } catch (e) {}
      }
      
      // If harvest and photo selected from local file, upload and attach
      if (formData.harvest && harvestPhotoFile) {
        try {
          // Downscale main and thumbnail
          const main = await downscaleImage(harvestPhotoFile, 1600);
          const thumb = await buildThumbnail(harvestPhotoFile);
          // Validate size
          if (main.blob.size > 10 * 1024 * 1024) {
            throw new Error('Downscaled image exceeds 10MB');
          }
          // Determine harvestId from the just-created/updated hunt
          let harvestId = updatedHuntObj?.harvest_details?.id || null;
          try {
            const targetHuntId = effectiveHuntId || huntId;
            if (!harvestId && targetHuntId) {
              const fresh = await huntService.getHunt(targetHuntId);
              harvestId = fresh?.harvest_details?.id;
            }
          } catch (_) {}
          // Brief retry if still missing (backend may create Harvest atomically after PUT)
          if (!harvestId && (effectiveHuntId || huntId)) {
            try {
              await new Promise(r => setTimeout(r, 300));
              const fresh2 = await huntService.getHunt(effectiveHuntId || huntId);
              harvestId = fresh2?.harvest_details?.id || harvestId;
            } catch (_) {}
          }
          if (!harvestId) {
            // Fallback: fetch hunts and pick the most recent
            const hunts = await huntService.getHunts();
            const latest = hunts && hunts.length ? hunts[0] : null;
            harvestId = latest?.harvest_details?.id || null;
          }
          if (!harvestId) {
            console.warn('No harvest id found to attach photo');
          } else {
            // Include sha for dedupe pre-check (compute on original file bytes)
            const computeSha256Hex = async (blob) => {
              const buf = await blob.arrayBuffer();
              const digest = await window.crypto.subtle.digest('SHA-256', buf);
              const arr = Array.from(new Uint8Array(digest));
              return arr.map(b => b.toString(16).padStart(2, '0')).join('');
            };
            let shaHex = null;
            try { shaHex = await computeSha256Hex(harvestPhotoFile); } catch (_) { shaHex = null; }

            // Call init with sha; if reuse, attach immediately
            const init = await huntService.initHarvestPhotoUpload(harvestId, harvestPhotoFile.name, harvestPhotoFile.type || 'image/jpeg', shaHex || undefined);
            if (init && init.reuse && init.asset && init.asset.id) {
              await huntService.attachHarvestPhoto(harvestId, { asset_id: init.asset.id, caption: harvestPhotoCaption || '' });
              try { show({ title: 'Photo already exists', body: 'Reused your existing photo. No upload performed.', variant: 'info' }); } catch (e) {}
              return;
            }
            // Upload blobs
            await fetch(init.upload_url, { method: 'PUT', body: main.blob, headers: { 'Content-Type': harvestPhotoFile.type || 'image/jpeg' } });
            await fetch(init.thumb_upload_url, { method: 'PUT', body: thumb.blob, headers: { 'Content-Type': harvestPhotoFile.type || 'image/jpeg' } });
            // Finalize asset with sha (original file), then attach via asset_id for dedupe
            const finalized = await (await import('../../../services/assetsService')).assetsService.finalize({
              scope: 'harvest-photo',
              object_key: init.object_key,
              thumb_object_key: init.thumb_object_key,
              size_bytes: main.blob.size,
              sha256: shaHex || undefined,
            });
            const assetId = finalized?.id;
            if (assetId) {
              await huntService.attachHarvestPhoto(harvestId, {
                asset_id: assetId,
                caption: harvestPhotoCaption || ''
              });
            } else {
              // Fallback to direct object keys
              await huntService.attachHarvestPhoto(harvestId, {
                object_key: init.object_key,
                thumb_object_key: init.thumb_object_key,
                caption: harvestPhotoCaption,
                width: main.width,
                height: main.height,
                size_bytes: main.blob.size
              });
            }
          }
        } catch (uploadErr) {
          console.error('Failed to upload harvest photo', uploadErr);
        }
      } else if (formData.harvest && selectedAsset && selectedAsset.id) {
        // If an existing asset was selected, attach by asset_id
        try {
          let harvestId = updatedHuntObj?.harvest_details?.id || null;
          try {
            const targetHuntId = effectiveHuntId || huntId;
            if (!harvestId && targetHuntId) {
              const fresh = await huntService.getHunt(targetHuntId);
              harvestId = fresh?.harvest_details?.id;
            }
          } catch (_) {}
          if (!harvestId && (effectiveHuntId || huntId)) {
            try {
              await new Promise(r => setTimeout(r, 300));
              const fresh2 = await huntService.getHunt(effectiveHuntId || huntId);
              harvestId = fresh2?.harvest_details?.id || harvestId;
            } catch (_) {}
          }
          if (!harvestId) {
            const hunts = await huntService.getHunts();
            const latest = hunts && hunts.length ? hunts[0] : null;
            harvestId = latest?.harvest_details?.id || null;
          }
          if (!harvestId) {
            console.warn('No harvest id found to attach existing asset');
          } else {
            await huntService.attachHarvestPhoto(
              harvestId,
              { asset_id: selectedAsset.id, caption: harvestPhotoCaption || '' }
            );
          }
        } catch (attachErr) {
          console.error('Failed to attach existing photo asset', attachErr);
        }
      }
      
      console.log('=== HUNT SUBMISSION COMPLETE ===');
      console.log('isFromSignOut:', isFromSignOut);
      console.log('signOutDetails:', signOutDetails);
      console.log('huntLocationId:', huntLocationId);
      console.log('prefillData:', location.state?.prefillData);
      
      // If this hunt was from a sign-out, automatically sign the user back in
      if (isFromSignOut && signOutDetails) {
        console.log('🌤️ Starting auto sign-in process...');
        
        // Get the location ID from prefillData or available locations
        let locationId = huntLocationId;
        
        if (!locationId && location.state?.prefillData) {
          const prefillData = location.state.prefillData;
          console.log('Location ID from prefillData:', prefillData.hunt_location);
          // Prefer explicit hunt_location from prefill
          if (prefillData.hunt_location) {
            locationId = prefillData.hunt_location;
          } else {
            // Legacy fallback: try matching by label
            const foundLocation = availableLocations.find(loc => 
              loc.label === prefillData.location || loc.value === prefillData.location
            );
            locationId = foundLocation?.locationId;
          }
          console.log('Resolved locationId:', locationId);
        }
        
        if (locationId) {
          console.log('✅ Found location ID:', locationId);
          console.log('Date:', location.state?.prefillData?.date);
          console.log('Time period:', signOutDetails.time_period);
          
          try {
            const dateYmd = toLocalYMD(location.state?.prefillData?.date || new Date());
            await HuntSignOutService.signInLocation(
              locationId,
              dateYmd,
              signOutDetails.time_period
            );
            console.log('✅ User automatically signed back in after hunt completion');
            // Emit global event so other views can refresh
            try {
              emitHuntCompleted({
                locationId,
                date: dateYmd,
                timePeriod: signOutDetails.time_period,
                userId: signOutDetails.user_id || null,
                clubId: (function(){
                  const sel = availableLocations.find(o => o.locationId === locationId);
                  return sel?.clubId || null;
                })()
              });
            } catch (_) {}
          } catch (signInError) {
            console.error('Failed to auto sign-in:', signInError);
          }
        } else {
          console.log('❌ Could not determine location ID for auto sign-in');
        }
      }
      
      navigate('/hunts');
    } catch (error) {
      setError('Failed to save hunt');
      console.error('Error submitting hunt:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleBack = () => {
    navigate('/hunts');
  };
  
  if (loading && huntId) {
    return (
      <div className="hunts-shell hunts-shell--form" data-testid="hunt-log-page">
        <div className="hunts-shell__loading">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span>Loading hunt data…</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="hunts-shell hunts-shell--form" data-testid="hunt-log-page">
      <svg className="hunts-shell__bg-grid" aria-hidden="true">
        <defs>
          <pattern id="hunt-log-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hunt-log-grid)" />
      </svg>

      <div className="hunts-shell__inner">
        <header className="hunts-shell__header">
          <HuntSectionHeader label="Field journal" />
          <div className="hunts-shell__header-main">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <YButton
                variant="secondary"
                onClick={handleBack}
                className="hunts-shell__back-btn d-flex align-items-center"
              >
                <FaArrowLeft className="me-2" aria-hidden="true" />
                Back
              </YButton>
              <h1 className="hunts-shell__title mb-0 d-flex align-items-center">
                <GiDeerTrack className="me-2" aria-hidden="true" />
                {huntId ? 'Edit Hunt' : 'Log Hunt'}
              </h1>
            </div>
          </div>
        </header>

        {error ? (
          <div className="hunts-shell__error" role="alert">
            {error}
          </div>
        ) : null}

        <Form onSubmit={handleSubmit}>
          <section className="hunts-shell__form-section">
            <HuntSectionHeader label="When & where" title="Hunt details" />
            <YCard className="hunts-shell__form-card">
                <Row>
                  <Col md={6}>
                    {isV2 ? (
                      <YFormField labelClassName={formLabelClass} label="Date" htmlFor="hunt-date" required error={errors.date}>
                        <YInput id="hunt-date" type="date" value={formData.date} onChange={(e) => handleInputChange('date', e.target.value)} />
                      </YFormField>
                    ) : (
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <FaCalendar className="me-2" />
                          Date
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.date}
                          onChange={(e) => handleInputChange('date', e.target.value)}
                          max={toLocalYMD(new Date())}
                          isInvalid={!!errors.date}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.date}
                        </Form.Control.Feedback>
                      </Form.Group>
                    )}
                  </Col>
                  <Col md={6}>
                    {isV2 ? (
                      <YFormField labelClassName={formLabelClass} label="Location" htmlFor="hunt-location" required error={errors.location}>
                        <YSelect id="hunt-location" value={formData.location} onChange={(e) => handleInputChange('location', e.target.value)}>
                          {(() => {
                            const groups = {};
                            availableLocations.forEach(opt => {
                              const g = opt.group || 'Other';
                              groups[g] = groups[g] || [];
                              groups[g].push(opt);
                            });
                            return Object.entries(groups).map(([groupLabel, opts]) => (
                              <optgroup key={groupLabel} label={groupLabel}>
                                {opts.map(o => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </optgroup>
                            ));
                          })()}
                        </YSelect>
                        {(() => {
                          const selected = availableLocations.find(o => o.value === formData.location);
                          if (selected && selected.isOtherClub) {
                            return (
                              <YInput
                                id="hunt-location-other"
                                type="text"
                                className="mt-2"
                                placeholder="Describe the spot (e.g., South Ridge)"
                                value={otherClubSpotName}
                                onChange={(e) => setOtherClubSpotName(e.target.value)}
                              />
                            );
                          }
                          return null;
                        })()}
                      </YFormField>
                    ) : (
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <FaMapMarkerAlt className="me-2" />
                          Location
                        </Form.Label>
                        <Form.Select
                          value={formData.location}
                          onChange={(e) => handleInputChange('location', e.target.value)}
                          isInvalid={!!errors.location}
                        >
                          {(() => {
                            const groups = {};
                            availableLocations.forEach(opt => {
                              const g = opt.group || 'Other';
                              groups[g] = groups[g] || [];
                              groups[g].push(opt);
                            });
                            return Object.entries(groups).map(([groupLabel, opts]) => (
                              <optgroup key={groupLabel} label={groupLabel}>
                                {opts.map(o => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </optgroup>
                            ));
                          })()}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.location}
                        </Form.Control.Feedback>
                        {(() => {
                          const selected = availableLocations.find(o => o.value === formData.location);
                          if (selected && selected.isOtherClub) {
                            return (
                              <Form.Control
                                className="mt-2"
                                type="text"
                                placeholder="Describe the spot (e.g., South Ridge)"
                                value={otherClubSpotName}
                                onChange={(e) => setOtherClubSpotName(e.target.value)}
                              />
                            );
                          }
                          return null;
                        })()}
                      </Form.Group>
                    )}
                  </Col>
                </Row>

                <div className="mb-3">
                  {isV2 ? (
                    <YFormField label="Time Periods Hunted" htmlFor="time-periods" error={errors.time_periods} labelClassName={formLabelClass}>
                      <div className="d-flex flex-wrap gap-2">
                        {timePeriodOptions.map(option => (
                          <YCheckbox
                            key={option.value}
                            id={`time-${option.value}`}
                            label={option.label}
                            checked={formData.time_periods.includes(option.value)}
                            onChange={() => handleTimePeriodChange(option.value)}
                          />
                        ))}
                      </div>
                    </YFormField>
                  ) : (
                    <Form.Group className="mb-3">
                      <Form.Label>Time Periods Hunted</Form.Label>
                      <div className="d-flex flex-wrap gap-2">
                        {timePeriodOptions.map(option => (
                          <Form.Check
                            key={option.value}
                            type="checkbox"
                            id={`time-${option.value}`}
                            label={option.label}
                            checked={formData.time_periods.includes(option.value)}
                            onChange={() => handleTimePeriodChange(option.value)}
                            inline
                          />
                        ))}
                      </div>
                      {errors.time_periods && (
                        <div className="text-danger small mt-1">{errors.time_periods}</div>
                      )}
                    </Form.Group>
                  )}
                </div>
                
                {formData.time_periods && formData.time_periods.length > 0 && (
                  <>
                  <HuntSectionHeader label="Conditions" title="Weather capture" />
                  <YCard className="mb-3 hunts-shell__form-card" headerVariant="muted" header={<h6 className="mb-0">Weather data</h6>}>
                      <small className="text-muted">
                        Weather information will be automatically captured for:
                      </small>
                      <div className="mt-2">
                        {formData.time_periods.map(period => (
                          <div key={period} className="d-flex justify-content-between align-items-center">
                            <span>{period.charAt(0).toUpperCase() + period.slice(1)}</span>
                            <small className="text-muted">
                              {period === 'morning' ? '09:00' : 
                               period === 'midday' ? '12:00' : 
                               period === 'afternoon' ? '16:00' : '12:00'}
                            </small>
                          </div>
                        ))}
                      </div>
                      {weatherLoading && (
                        <div className="mt-3 pt-3 border-top">
                          <div className="text-center">
                            <div className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Loading weather...</span>
                            </div>
                            <small className="ms-2">Loading weather data...</small>
                          </div>
                        </div>
                      )}
                      {Object.keys(weatherData).length > 0 && !weatherLoading && (
                        <div className="mt-3 pt-3 border-top">
                          <h6 className="mb-2">Weather Preview</h6>
                          {Object.entries(weatherData).map(([period, weather]) => (
                            <div key={period} className="d-flex justify-content-between align-items-center mb-1">
                              <span>{period.charAt(0).toUpperCase() + period.slice(1)}:</span>
                              <span className="text-muted">
                                {weather.temperature}°F, {weather.wind_speed} mph {weather.wind_direction_text}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                  </YCard>
                  </>
                )}

                <HuntSectionHeader label="Observations" title="Deer & activity" />
                <Row>
                  <Col md={4}>
                    {isV2 ? (
                      <YFormField label="Does Seen" htmlFor="does-seen" error={errors.does_seen} labelClassName={formLabelClass}>
                        <YInput id="does-seen" type="number" min={0} value={formData.does_seen} onChange={(e) => handleInputChange('does_seen', parseInt(e.target.value) || 0)} />
                      </YFormField>
                    ) : (
                      <Form.Group className="mb-3">
                        <Form.Label>Does Seen</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          value={formData.does_seen}
                          onChange={(e) => handleInputChange('does_seen', parseInt(e.target.value) || 0)}
                          isInvalid={!!errors.does_seen}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.does_seen}
                        </Form.Control.Feedback>
                      </Form.Group>
                    )}
                  </Col>
                  <Col md={4}>
                    {isV2 ? (
                      <YFormField labelClassName={formLabelClass} label="Bucks Seen" htmlFor="bucks-seen" error={errors.bucks_seen}>
                        <YInput id="bucks-seen" type="number" min={0} value={formData.bucks_seen} onChange={(e) => handleInputChange('bucks_seen', parseInt(e.target.value) || 0)} />
                      </YFormField>
                    ) : (
                      <Form.Group className="mb-3">
                        <Form.Label>Bucks Seen</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          value={formData.bucks_seen}
                          onChange={(e) => handleInputChange('bucks_seen', parseInt(e.target.value) || 0)}
                          isInvalid={!!errors.bucks_seen}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.bucks_seen}
                        </Form.Control.Feedback>
                      </Form.Group>
                    )}
                  </Col>
                  <Col md={4}>
                    {isV2 ? (
                      <YFormField labelClassName={formLabelClass} label="Fawns Seen" htmlFor="fawns-seen" error={errors.fawns_seen}>
                        <YInput id="fawns-seen" type="number" min={0} value={formData.fawns_seen} onChange={(e) => handleInputChange('fawns_seen', parseInt(e.target.value) || 0)} />
                      </YFormField>
                    ) : (
                      <Form.Group className="mb-3">
                        <Form.Label>Fawns Seen</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          value={formData.fawns_seen}
                          onChange={(e) => handleInputChange('fawns_seen', parseInt(e.target.value) || 0)}
                          isInvalid={!!errors.fawns_seen}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.fawns_seen}
                        </Form.Control.Feedback>
                      </Form.Group>
                    )}
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    {isV2 ? (
                      <YCheckbox
                        id="shot-taken"
                        label="Shot Taken"
                        checked={formData.shot_taken}
                        onChange={(checked) => handleInputChange('shot_taken', checked)}
                      />
                    ) : (
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          id="shot-taken"
                          label="Shot Taken"
                          checked={formData.shot_taken}
                          onChange={(e) => handleInputChange('shot_taken', e.target.checked)}
                        />
                      </Form.Group>
                    )}
                  </Col>
                  <Col md={6}>
                    {isV2 ? (
                      <YCheckbox
                        id="harvest"
                        label="Harvest"
                        checked={formData.harvest}
                        onChange={(checked) => handleInputChange('harvest', checked)}
                      />
                    ) : (
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          id="harvest"
                          label="Harvest"
                          checked={formData.harvest}
                          onChange={(e) => handleInputChange('harvest', e.target.checked)}
                        />
                      </Form.Group>
                    )}
                  </Col>
                </Row>
                
                {/* Harvest Details Form */}
                {formData.harvest && (
                  <>
                  <HuntSectionHeader label="Harvest" title="Harvest details" />
                  <YCard className="mb-3 hunts-shell__form-card" headerVariant="muted" header={<h6 className="mb-0">Harvest details</h6>}>
                      <Row>
                        <Col md={4}>
                          {isV2 ? (
                            <YFormField labelClassName={formLabelClass} label="Deer Type" htmlFor="harvest-deer-type" error={errors.harvest_deer_type}>
                              <YSelect id="harvest-deer-type" value={harvestData.deer_type} onChange={(e) => handleHarvestChange('deer_type', e.target.value)}>
                                <option value="buck">Buck</option>
                                <option value="doe">Doe</option>
                              </YSelect>
                            </YFormField>
                          ) : (
                            <Form.Group className="mb-3">
                              <Form.Label>Deer Type</Form.Label>
                              <Form.Select
                                value={harvestData.deer_type}
                                onChange={(e) => handleHarvestChange('deer_type', e.target.value)}
                                isInvalid={!!errors.harvest_deer_type}
                              >
                                <option value="buck">Buck</option>
                                <option value="doe">Doe</option>
                              </Form.Select>
                              <Form.Control.Feedback type="invalid">
                                {errors.harvest_deer_type}
                              </Form.Control.Feedback>
                            </Form.Group>
                          )}
                        </Col>
                        <Col md={4}>
                          {isV2 ? (
                            <YFormField labelClassName={formLabelClass} label="Method" htmlFor="harvest-method">
                              <YSelect id="harvest-method" value={harvestData.method} onChange={(e) => handleHarvestChange('method', e.target.value)}>
                                <option value="gun">Gun</option>
                                <option value="bow">Bow</option>
                              </YSelect>
                            </YFormField>
                          ) : (
                            <Form.Group className="mb-3">
                              <Form.Label>Method</Form.Label>
                              <Form.Select
                                value={harvestData.method}
                                onChange={(e) => handleHarvestChange('method', e.target.value)}
                              >
                                <option value="gun">Gun</option>
                                <option value="bow">Bow</option>
                              </Form.Select>
                            </Form.Group>
                          )}
                        </Col>
                        <Col md={4}>
                          {isV2 ? (
                            <YFormField labelClassName={formLabelClass} label="Weight (lbs)" htmlFor="harvest-weight">
                              <YInput id="harvest-weight" type="number" min={0} step={0.1} value={harvestData.weight} onChange={(e) => handleHarvestChange('weight', e.target.value)} placeholder="Optional" />
                            </YFormField>
                          ) : (
                            <Form.Group className="mb-3">
                              <Form.Label>
                                <FaWeight className="me-2" />
                                Weight (lbs)
                              </Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                step="0.1"
                                value={harvestData.weight}
                                onChange={(e) => handleHarvestChange('weight', e.target.value)}
                                placeholder="Optional"
                              />
                            </Form.Group>
                          )}
                        </Col>
                      </Row>
                      {harvestData.deer_type === 'buck' && (
                        isV2 ? (
                          <YFormField labelClassName={formLabelClass} label="Points" htmlFor="harvest-points" error={errors.harvest_points}>
                            <YInput id="harvest-points" type="number" min={0} value={harvestData.points} onChange={(e) => handleHarvestChange('points', e.target.value)} placeholder="Number of antler points" />
                          </YFormField>
                        ) : (
                          <Form.Group className="mb-3">
                            <Form.Label>
                              <FaBullseye className="me-2" />
                              Points
                            </Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              value={harvestData.points}
                              onChange={(e) => handleHarvestChange('points', e.target.value)}
                              isInvalid={!!errors.harvest_points}
                              placeholder="Number of antler points"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.harvest_points}
                            </Form.Control.Feedback>
                          </Form.Group>
                        )
                      )}
                      {isV2 ? (
                        <YFormField labelClassName={formLabelClass} label="Notable Features" htmlFor="harvest-notes">
                          <YTextarea id="harvest-notes" rows={3} value={harvestData.notable_features} onChange={(e) => handleHarvestChange('notable_features', e.target.value)} placeholder="Any notable features or characteristics..." />
                        </YFormField>
                      ) : (
                        <Form.Group className="mb-3">
                          <Form.Label>Notable Features</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows="3"
                            value={harvestData.notable_features}
                            onChange={(e) => handleHarvestChange('notable_features', e.target.value)}
                            placeholder="Any notable features or characteristics..."
                          />
                        </Form.Group>
                      )}
                      
                      {/* Harvest Photo Uploader */}
                      <Form.Group className="mb-3">
                        <Form.Label>Harvest Photo (optional)</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files && e.target.files[0];
                            setSelectedAsset(null);
                            setHarvestPhotoFile(file || null);
                            if (file) {
                              const url = URL.createObjectURL(file);
                              setHarvestPhotoPreview(url);
                            } else {
                              setHarvestPhotoPreview(null);
                            }
                          }}
                        />
                        <div className="mt-2 d-flex gap-2">
                          <YButton
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowAssetPicker(true)}
                          >
                            Choose from My Photos
                          </YButton>
                          {(harvestPhotoPreview || selectedAsset) && (
                            <YButton
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => { setHarvestPhotoFile(null); setSelectedAsset(null); setHarvestPhotoPreview(null); }}
                            >
                              Clear
                            </YButton>
                          )}
                        </div>
                        {harvestPhotoPreview && (
                          <div className="mt-2">
                            <RBImage src={harvestPhotoPreview} thumbnail style={{ maxWidth: 200 }} alt="Harvest preview" />
                          </div>
                        )}
                        <Form.Control
                          className="mt-2"
                          type="text"
                          placeholder="Caption (optional)"
                          value={harvestPhotoCaption}
                          onChange={(e) => setHarvestPhotoCaption(e.target.value)}
                        />
                        <div className="text-muted small mt-1">Max 10MB after downscale. JPEG/PNG recommended.</div>
                      </Form.Group>
                  </YCard>
                  </>
                )}

                <HuntSectionHeader label="Notes" title="Additional notes" />
                {isV2 ? (
                  <YFormField label="Notes" htmlFor="hunt-notes" labelClassName={formLabelClass}>
                    <YTextarea id="hunt-notes" rows={3} value={formData.notes} onChange={(e) => handleInputChange('notes', e.target.value)} placeholder="Additional notes about the hunt..." />
                  </YFormField>
                ) : (
                  <Form.Group className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows="3"
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      placeholder="Additional notes about the hunt..."
                    />
                  </Form.Group>
                )}
                
                <div className="hunts-shell__form-footer">
                  <YButton variant="secondary" onClick={handleBack}>Cancel</YButton>
                  <YButton variant="primary" type="submit" disabled={loading}>
                    {loading ? 'Saving...' : (hunt ? 'Update Hunt' : 'Log Hunt')}
                  </YButton>
                </div>
            </YCard>
          </section>
      </Form>
      <AssetPickerModal
        show={showAssetPicker}
        onHide={() => setShowAssetPicker(false)}
        onSelect={(asset) => {
          setSelectedAsset(asset);
          setHarvestPhotoFile(null);
          setHarvestPhotoPreview(asset.thumb_url || asset.url || null);
          setShowAssetPicker(false);
        }}
      />
      </div>
    </div>
  );
};

export default HuntLogPage;
 
// Asset picker modal portal
// Place modal at the end to avoid layout shifts
// Note: uses existing AssetPickerModal component
export const HuntLogAssetPicker = ({ show, onHide, onSelect }) => (
  <AssetPickerModal show={show} onHide={onHide} onSelect={onSelect} />
);

